import React, { useState, useCallback, useEffect, lazy, Suspense, memo } from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/_core/hooks/useAuth';
// Removed V1 DinoGame - using only V2
import DashboardHeader from '@/components/DashboardHeader';
import { ChampionshipBanner } from '@/components/ChampionshipBanner';
// Removed engine flag - using only V2
import { useInputMode } from '@/hooks/useInputMode';
import { useGameSettings } from '@/contexts/GameSettingsContext';
import { getBadgeById, type StreakState } from '@/utils/dailyStreak';
import { formatNumber } from '@/utils/formatters';
import styles from '@/styles/chrome.module.css';
// useShareCard is a hook (not a component) — import directly (not lazy)
import { useShareCard } from '@/components/ShareCardGenerator';
import { SupporterBadgeModal } from '@/components/SupporterBadgeModal';
import { GameOverPanel } from '@/components/GameOverPanel';
import { DailyPulseTicker } from '@/components/DailyPulseTicker';
import { GameOverDashboard } from '@/components/GameOverDashboard';
import { GameWrapper } from '@/components/GameWrapper';
// EngineDiagnostics stays below the stable 100svh game shell and loads after critical gameplay UI.
const EngineDiagnostics = lazy(() => import('@/components/EngineDiagnostics').then(m => ({ default: m.EngineDiagnostics })));
import { ChromeStatusBar } from '@/components/ChromeStatusBar';
import { useGameState } from '@/contexts/GameStateContext';

// Lazy-load SystemMenu (only loaded when gear icon clicked)
const SystemMenu = lazy(() => import('@/components/SystemMenu').then(m => ({ default: m.SystemMenu })));

// V2 engine - now the only engine
const DinoGameV2 = lazy(() => import('@/components/DinoGameV2').then(m => ({ default: m.DinoGame })));

// Lazy-load below-fold components to keep game TTI under 500ms
// These are not needed until after the first interaction, so defer their bundle
const ShareScore = lazy(() => import('@/components/ShareScore').then(m => ({ default: m.ShareScore })));
const DailyChallenge = lazy(() => import('@/components/DailyChallenge').then(m => ({ default: m.DailyChallenge })));

/**
 * Chrome Dino Game - Main Page
 * Chrome-Native UI: Single SystemMenu modal, vanilla CSS, minimal flat design
 * Strict adherence to Google Chrome "No Internet" page aesthetic
 */

interface StreakToastProps {
  badgeId?: string;
  streakCount?: number;
  onDismiss: () => void;
}

const StreakCelebrationToast = memo(({ badgeId, streakCount, onDismiss }: StreakToastProps) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4500);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const badge = badgeId ? getBadgeById(badgeId) : undefined;
  const title = badge ? `Badge unlocked: ${badge.name}` : `${streakCount}-day streak!`;
  const subtitle = badge
    ? badge.description
    : 'Keep coming back daily to earn the Veteran badge.';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '56px',
        right: '16px',
        zIndex: 35,
        background: '#ffffff',
        border: '1px solid #d3d3d3',
        padding: '12px 16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        fontFamily: 'system-ui, -apple-system, Roboto, Arial, sans-serif',
        fontSize: '13px',
        maxWidth: '280px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <div style={{ fontSize: '22px' }}>⚡</div>
      <div>
        <div style={{ fontWeight: 500, color: '#222222', marginBottom: '2px' }}>{title}</div>
        <div style={{ fontSize: '12px', color: '#666666' }}>{subtitle}</div>
      </div>
    </div>
  );
});
StreakCelebrationToast.displayName = 'StreakCelebrationToast';

// Global Rank Celebration Toast — triggered after successful score sync
const GlobalRankToast = memo(({ rank, score, onDismiss }: { rank: number; score: number; onDismiss: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  
  // Tier label based on rank
  let tierLabel = `Global Rank #${rank}`;
  let icon = '🌍';
  if (rank === 1) { tierLabel = '🥇 #1 Global Champion!'; icon = '🏆'; }
  else if (rank <= 10) { tierLabel = `Top 10 Globally! (#${rank})`; icon = '⭐'; }
  else if (rank <= 100) { tierLabel = `Top 100 Globally! (#${rank})`; icon = '🎆'; }
  else if (rank <= 1000) { tierLabel = `Top 1000 Globally (#${rank})`; icon = '🚀'; }
  
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '120px',
        right: '16px',
        zIndex: 36,
        background: 'linear-gradient(135deg, #1557b0, #1a73e8)',
        padding: '14px 18px',
        boxShadow: '0 4px 16px rgba(26,115,232,0.35)',
        fontFamily: 'system-ui, -apple-system, Roboto, sans-serif',
        fontSize: '13px',
        maxWidth: '300px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderRadius: '6px',
        color: '#fff',
      }}
    >
      <div style={{ fontSize: '26px' }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: '2px' }}>{tierLabel}</div>
        <div style={{ fontSize: '11px', opacity: 0.92 }}>Score: {formatNumber(score)} · Synced to cloud</div>
      </div>
    </div>
  );
});
GlobalRankToast.displayName = 'GlobalRankToast';

// Coins Added Toast — fires after rewarded ad completes
const CoinsAddedToast = memo(({ amount, onDismiss }: { amount: number; onDismiss: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3500);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '56px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 80,
        background: 'linear-gradient(135deg, #1e8e3e, #34a853)',
        color: '#ffffff',
        padding: '10px 20px',
        borderRadius: '24px',
        boxShadow: '0 4px 16px rgba(30,142,62,0.45)',
        fontFamily: 'system-ui, -apple-system, Roboto, sans-serif',
        fontSize: '14px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        whiteSpace: 'nowrap',
        animation: 'rewardFadeIn 200ms cubic-bezier(0.23,1,0.32,1)',
      }}
    >
      <span style={{ fontSize: '18px' }}>🪙</span>
      +{formatNumber(amount)} Coins Added!
    </div>
  );
});
CoinsAddedToast.displayName = 'CoinsAddedToast';

// One-Click Share Button: generates themed card and triggers native share
const OneClickShareButton = memo(({ score, highScore }: { score: number; highScore: number }) => {
  const { generateAndShare } = useShareCard();
  const [sharing, setSharing] = useState(false);

  const handleShare = useCallback(async () => {
    setSharing(true);
    await generateAndShare(score, highScore);
    setSharing(false);
  }, [generateAndShare, score, highScore]);

  return (
    <button
      onClick={handleShare}
      disabled={sharing}
      style={{
        width: '100%',
        marginTop: '12px',
        padding: '10px 16px',
        background: sharing ? '#999' : '#FF6B35',
        color: '#ffffff',
        border: 'none',
        borderRadius: '4px',
        cursor: sharing ? 'wait' : 'pointer',
        fontSize: '13px',
        fontWeight: '600',
        transition: 'background 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}
      aria-label="Share themed score card"
    >
      {sharing ? 'Generating...' : '✨ One-Click Share (Themed Card)'}
    </button>
  );
});
OneClickShareButton.displayName = 'OneClickShareButton';

export default function Home() {
  // Browser detection for analytics
  useEffect(() => {
    const userAgent = navigator.userAgent;
    let browser = 'Other';
    
    if (userAgent.includes('Edg')) browser = 'Edge';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
    else if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    
    const deviceType = /Mobile|Android|iPhone|iPad/.test(userAgent) ? 'mobile' : 'desktop';
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        'browser': browser,
        'device_type': deviceType
      });
    }
  }, []);

  // Game state
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(
    parseInt(localStorage.getItem('dinoHighScore') || '0')
  );
  const [coins, setCoins] = useState(
    parseInt(localStorage.getItem('dinoCoins') || '0')
  );
  const [gamesPlayed, setGamesPlayed] = useState(
    parseInt(localStorage.getItem('dinoGamesPlayed') || '0')
  );
  const [showShareModal, setShowShareModal] = useState(false);
  const [supporterBadge, setSupporterBadge] = useState<{ score: number; percentile: number } | null>(null);
  const [coinsToast, setCoinsToast] = useState<{ amount: number } | null>(null);
  // Seed from the last completed game run (used to generate the Challenge Link)
  const [lastGameSeed, setLastGameSeed] = useState<number | undefined>(undefined);
  const [challengeLinkCopied, setChallengeLinkCopied] = useState(false);

  // Fetch top-100 scores for percentile calculation (cached 30s)
  const topScoresQuery = trpc.leaderboard.top.useQuery({ limit: 100 }, {
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  
  // Dynamic Open Graph image tag injection — updates whenever share modal opens
  useEffect(() => {
    if (!showShareModal) return;
    const score = lastGameScore;
    const badge = score >= 1000 ? 'milestone' : 'standard';
    const ogUrl = `${window.location.origin}/api/og?score=${score}&badge=${badge}&theme=${document.documentElement.getAttribute('data-theme') || 'light'}`;
    
    let ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement | null;
    let twitterImage = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement | null;
    
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      document.head.appendChild(twitterImage);
    }
    
    // Save previous values so we can restore on close
    const prevOg = ogImage.content;
    const prevTwitter = twitterImage.content;
    
    ogImage.content = ogUrl;
    twitterImage.content = ogUrl;
    
    return () => {
      if (ogImage) ogImage.content = prevOg;
      if (twitterImage) twitterImage.content = prevTwitter;
    };
  }, [showShareModal]);
  const [showSystemMenu, setShowSystemMenu] = useState(false);
  const [systemMenuTab, setSystemMenuTab] = useState<'profile' | 'difficulty' | 'cosmetics' | 'marketplace' | 'replays' | undefined>(undefined);
  const [lastGameScore, setLastGameScore] = useState(0);

  // Deep-link: Open SystemMenu to specific tab via ?tab= query param
  // Also consume ?d= difficulty param and ?seed= integer param from Challenge Links
  const { updateDifficultyMode, settings, addAchievement } = useGameSettings();
  const [incomingSeed, setIncomingSeed] = useState<number | undefined>(undefined);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const difficultyParam = params.get('d');
    const seedParam = params.get('seed');

    // Apply seed deep-link (?seed=integer) — from Challenge Link shares
    if (seedParam) {
      const parsedSeed = parseInt(seedParam, 10);
      if (!isNaN(parsedSeed) && parsedSeed > 0) {
        setIncomingSeed(parsedSeed);
        // GA4 tracking for seed challenge link attribution
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'challenge_link_accepted', {
            'seed': parsedSeed,
            'timestamp': new Date().toISOString(),
          });
        }
      }
    }

    // Apply difficulty deep-link (?d=easy|normal|hard) — from difficulty shares
    if (difficultyParam && ['easy', 'normal', 'hard'].includes(difficultyParam)) {
      updateDifficultyMode(difficultyParam as 'easy' | 'normal' | 'hard');
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'challenge_link_accepted', {
          'difficulty': difficultyParam,
          'timestamp': new Date().toISOString(),
        });
      }
    }

    // Support legacy 'skins' alias by mapping to 'cosmetics'
    const normalizedTab = tab === 'skins' ? 'cosmetics' : tab;
    const validTabs = ['profile', 'difficulty', 'cosmetics', 'marketplace', 'replays'] as const;
    type ValidTab = (typeof validTabs)[number];
    if (normalizedTab && (validTabs as readonly string[]).includes(normalizedTab)) {
      setSystemMenuTab(normalizedTab as ValidTab);
      setShowSystemMenu(true);
    }

    // Clean URL without reloading (remove all query params)
    if (params.toString()) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [updateDifficultyMode]);

  // Engine feature flag (V1 stable / V2 Chromium-authentic)
  // V2 is now the only engine
  const engine = 'v2';
  // Game lifecycle state for ad placement (only show ads on game over, not during play)
  const { isGameOver, setIsGameOver, setCurrentAchievement, setShowAchievementToast } = useGameState();
  // Context-aware input mode: updates in real-time on first keydown, touchstart, or gamepadconnected
  const inputMode = useInputMode();
  const instructionText =
    inputMode === 'touch'   ? 'Tap to jump' :
    inputMode === 'gamepad' ? 'Press A to jump • Down to duck' :
                              'Press SPACE to jump • DOWN arrow to duck';

  // Streak celebration toast state
  const [streakUpdate, setStreakUpdate] = useState<StreakState | null>(null);

  // Listen for the global 'open-system-menu' event dispatched by ChampionshipBanner gear button
  useEffect(() => {
    const handler = () => setShowSystemMenu(true);
    window.addEventListener('open-system-menu', handler);
    return () => window.removeEventListener('open-system-menu', handler);
  }, []);

  // Helper: unlock an achievement and show the toast (only if not already unlocked)
  const unlockAchievement = useCallback((id: string) => {
    if (settings.achievements.includes(id)) return;
    addAchievement(id);
    setCurrentAchievement(id);
    setShowAchievementToast(true);
  }, [settings.achievements, addAchievement, setCurrentAchievement, setShowAchievementToast]);

  const handleScoreChange = useCallback((newScore: number) => {
    setScore(newScore);
    // Score > 0 means game has restarted — clear game-over state
    if (newScore > 0) setIsGameOver(false);
    // Score-based achievements (checked live during play)
    if (newScore >= 100) unlockAchievement('score_100');
    if (newScore >= 500) unlockAchievement('score_500');
    if (newScore >= 1000) unlockAchievement('score_1000');
  }, [setIsGameOver, unlockAchievement]);

  // Authentication state for cloud-sync of scores
  const { isAuthenticated } = useAuth();
  const [rankToast, setRankToast] = useState<{ rank: number; score: number } | null>(null);
  const [isNightMode, setIsNightMode] = useState(false);

  const handleNightModeChange = useCallback((isNight: boolean) => {
    setIsNightMode(isNight);
  }, []);
  
  // tRPC mutation for persisting score to MySQL
  const syncScoreMutation = trpc.leaderboard.sync.useMutation({
    onSuccess: (data) => {
      if (data.globalRank !== null && data.globalRank !== undefined) {
        setRankToast({ rank: data.globalRank, score: data.personalBest });
      }
      // GA4 tracking
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'score_synced_to_cloud', {
          'score': data.personalBest,
          'global_rank': data.globalRank,
          'timestamp': new Date().toISOString(),
        });
      }
    },
  });

  const handleGameOver = useCallback((finalScore: number, newHighScore: number, activeSeed?: number) => {
    setIsGameOver(true);
    setIsNightMode(false); // Reset night mode when game ends
    setHighScore(newHighScore);
    setLastGameScore(finalScore);
    // Store the seed so we can generate the Challenge Link on game over
    if (activeSeed !== undefined) setLastGameSeed(activeSeed);
    const newGamesPlayed = gamesPlayed + 1;
    setGamesPlayed(newGamesPlayed);
    localStorage.setItem('dinoGamesPlayed', newGamesPlayed.toString());
    // Achievement: first game ever played
    unlockAchievement('first_game');
    // Achievement: hard mode score
    if (settings.difficultyMode === 'hard' && finalScore >= 100) {
      unlockAchievement('hard_mode');
    }

    // Persist score to backend if user is authenticated
    if (isAuthenticated && finalScore > 0) {
      syncScoreMutation.mutate({
        score: finalScore,
        ambassadorCoinsEarned: 0,
        distanceRan: finalScore, // score === distance in this game engine
      });
    }

    // Gamified Tip Top Jar: show supporter badge when score is in top 10%
    // Uses live leaderboard data if available, otherwise falls back to a local
    // threshold (score >= 500 is roughly top 10% for casual players).
    if (finalScore > 0) {
      const topScores = topScoresQuery.data ?? [];
      let percentile = 100;
      if (topScores.length >= 10) {
        // Count how many top-100 scores the player beats
        const beaten = topScores.filter((entry: { score: number }) => finalScore > entry.score).length;
        percentile = Math.round(((topScores.length - beaten) / topScores.length) * 100);
      } else if (finalScore >= 500) {
        // Fallback: 500+ is a solid score for most players
        percentile = 10;
      }
      if (percentile <= 10) {
        // Stagger after share modal to avoid modal collision
        setTimeout(() => setSupporterBadge({ score: finalScore, percentile }), 800);
      }
    }

    // Sequential popups (ShareModal, RewardedAd, SupporterBadge) are suppressed.
    // GameOverDashboard is now the sole post-game overlay — one screen, one decision.
    // To re-enable individual popups, uncomment the blocks below and remove the dashboard.
    //
    // }
  }, [gamesPlayed, isAuthenticated, syncScoreMutation, topScoresQuery.data, unlockAchievement, settings.difficultyMode]);

  const handleCoinsEarned = useCallback((earned: number) => {
    if (earned > 0) {
      const newCoins = coins + earned;
      setCoins(newCoins);
      localStorage.setItem('dinoCoins', newCoins.toString());
      // Coin-based achievements
      if (newCoins >= 50) unlockAchievement('coins_50');
      if (newCoins >= 200) unlockAchievement('coins_200');
    }
  }, [coins, unlockAchievement]);

  return (
    <div className="flex-1 flex flex-col" style={{
      background: isNightMode ? '#202124' : 'var(--bg-color)',
      transition: 'background 1s ease',
    }}>
      {/* Site Identity Bar + controls are in ChampionshipBanner (App.tsx) */}
      {/* Streak updates from DashboardHeader are now handled via the streak useEffect below */}

      {/* Streak Celebration Toast */}
      {streakUpdate && streakUpdate.newBadgeUnlocked && (
        <StreakCelebrationToast
          badgeId={streakUpdate.newBadgeUnlocked}
          onDismiss={() => setStreakUpdate(null)}
        />
      )}
      {streakUpdate && !streakUpdate.newBadgeUnlocked && streakUpdate.count >= 2 && (
        <StreakCelebrationToast
          streakCount={streakUpdate.count}
          onDismiss={() => setStreakUpdate(null)}
        />
      )}
      
      {/* Global Rank Toast (from cloud sync) */}
      {rankToast && (
        <GlobalRankToast
          rank={rankToast.rank}
          score={rankToast.score}
          onDismiss={() => setRankToast(null)}
        />
      )}

      {/* SystemMenu - Lazy loaded */}
      {showSystemMenu && (
        <Suspense fallback={null}>
          <SystemMenu
            isOpen={showSystemMenu}
            onClose={() => {
              setShowSystemMenu(false);
              setSystemMenuTab(undefined);
            }}
            initialTab={systemMenuTab}
          />
        </Suspense>
      )}

      {/* Game Area — 100vh pristine viewport, top-aligned like native Chrome offline page */}
      <main
        id="adsense-excluded-gameplay"
        data-auto-ads-protected="gameplay"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight: '100svh',
          boxSizing: 'border-box',
          padding: '0 16px',
          paddingTop: '80px', /* position game canvas near top, matching Chrome offline page */
          paddingBottom: '64px', /* reserve space for the fixed score bar (~56px) + 8px breathing room */
          background: isNightMode ? '#202124' : 'var(--bg-color)',
          transition: 'background 1s ease',
        }}
      >
        {/* H1 for LCP — visible on cold load (Lighthouse sees it), fades out once game starts */}
        <h1
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: isNightMode ? 'rgba(255,255,255,0.55)' : 'rgba(83,83,83,0.7)',
            letterSpacing: '0.01em',
            fontFamily: 'var(--font-ui, Inter, Roboto, system-ui, sans-serif)',
            textAlign: 'center',
            userSelect: 'none',
            // Fade out once the game starts (score > 0 means the player has begun)
            opacity: score > 0 ? 0 : 1,
            height: score > 0 ? 0 : undefined,
            margin: score > 0 ? 0 : '0 0 6px 0',
            overflow: 'hidden',
            transition: 'opacity 0.4s ease, height 0.4s ease, margin 0.4s ease',
            pointerEvents: 'none',
          }}
          aria-hidden={score > 0}
        >
          Chrome Dino Game — Play Free Online
        </h1>

        {/* GameWrapper guards keyboard/gamepad inputs when GameOverDashboard is open */}
        <GameWrapper
          isModalOpen={isGameOver}
          onRestart={() => setIsGameOver(false)}
        >
        <div className="w-full max-w-[600px]">
          <Suspense fallback={<div style={{ height: 150, background: 'var(--bg-color)' }} />}>
            <DinoGameV2
              onGameOver={handleGameOver}
              onScoreChange={handleScoreChange}
              onCoinsEarned={handleCoinsEarned}
              onNightModeChange={handleNightModeChange}
            />
          </Suspense>
        </div>
        </GameWrapper>

      </main>
      {/* ── Engine Diagnostics — below-fold semantic content layer ── */}
      {/* Rendered for Googlebot; users scroll to discover */}
      {/* HUD stats (GAMES / HIGH SCORE / COINS) — fixed to the bottom of the browser viewport */}
      <div
        id="adsense-excluded-game-hud"
        data-auto-ads-protected="game-hud"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          background: isNightMode ? '#202124' : 'var(--bg-color)',
          borderTop: `1px solid ${isNightMode ? '#3c4043' : 'var(--border-color)'}`,
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'center',
          transition: 'background 1s ease',
        }}
      >
        <div style={{ width: '100%', maxWidth: '600px' }}>
          <div className="grid grid-cols-4 gap-2 text-center">
            {/* SCORE — live in-game value, resets to 0 between runs */}
            <div className="min-w-0">
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: isNightMode ? '#9aa0a6' : 'var(--text-color)',
                margin: '0 0 2px',
              }}>SCORE</p>
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '14px',
                fontWeight: 700,
                // Pulse amber while actively scoring; neutral when idle
                color: score > 0 ? (isNightMode ? '#f9ab00' : '#b06000') : (isNightMode ? '#bdc1c6' : 'var(--text-color)'),
                margin: 0,
                fontVariantNumeric: 'tabular-nums',
                transition: 'color 0.3s ease',
              }}>{formatNumber(score)}</p>
            </div>
            <div className="min-w-0">
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: isNightMode ? '#9aa0a6' : 'var(--text-color)',
                margin: '0 0 2px',
              }}>GAMES</p>
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '14px',
                fontWeight: 700,
                color: isNightMode ? '#bdc1c6' : 'var(--text-color)',
                margin: 0,
                fontVariantNumeric: 'tabular-nums',
              }}>{formatNumber(gamesPlayed)}</p>
            </div>
            <div className="min-w-0">
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: isNightMode ? '#9aa0a6' : 'var(--text-color)',
                margin: '0 0 2px',
              }}>HIGH SCORE</p>
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '14px',
                fontWeight: 700,
                color: isNightMode ? '#bdc1c6' : 'var(--text-color)',
                margin: 0,
                fontVariantNumeric: 'tabular-nums',
              }}>{formatNumber(highScore)}</p>
            </div>
            <div className="min-w-0">
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: isNightMode ? '#9aa0a6' : 'var(--text-color)',
                margin: '0 0 2px',
              }}>COINS</p>
              <p style={{
                fontFamily: 'var(--font-game)',
                fontSize: '14px',
                fontWeight: 700,
                color: '#1e8e3e',
                margin: 0,
                fontVariantNumeric: 'tabular-nums',
              }}>{formatNumber(coins)}</p>
            </div>
          </div>
        </div>
      </div>
      <Suspense fallback={<div style={{ height: '200px', background: isNightMode ? '#202124' : 'var(--bg-color)' }} />}>
        <EngineDiagnostics isNightMode={isNightMode} />
      </Suspense>

      {/* Share Score Modal — lazy loaded */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="rounded-lg shadow-lg max-w-sm w-full" style={{ background: 'var(--bg-color)', border: '1px solid var(--border-color)' }}>
            <div className="p-6">
              <Suspense fallback={<div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '13px' }}>Loading...</div>}>
                <ShareScore 
                  score={lastGameScore} 
                  highScore={highScore}
                  onClose={() => setShowShareModal(false)}
                />
              </Suspense>
              <OneClickShareButton score={lastGameScore} highScore={highScore} />
            </div>
          </div>
        </div>
      )}

      {/* Rewarded Ad Modal — v3.2 unified design */}
      {/* Supporter Badge Modal — kept for top-10% trigger but suppressed during dashboard */}
      {supporterBadge && !isGameOver && (
        <SupporterBadgeModal
          score={supporterBadge.score}
          percentile={supporterBadge.percentile}
          onClose={() => setSupporterBadge(null)}
        />
      )}

      {/* Coins Added Toast — fires after rewarded ad completes */}
      {coinsToast && (
        <CoinsAddedToast
          amount={coinsToast.amount}
          onDismiss={() => setCoinsToast(null)}
        />
      )}

      {/* ── GAME OVER DASHBOARD — sole post-game overlay ── */}
      <GameOverDashboard
        score={lastGameScore}
        highScore={highScore}
        isAuthenticated={isAuthenticated}
        isGameOver={isGameOver}
        topScores={topScoresQuery.data ?? []}
        lastGameSeed={lastGameSeed}
        gamesPlayed={gamesPlayed}
        onPlayAgain={() => {
          // Dismiss dashboard by clearing game-over state
          // The game canvas handles the actual restart via SPACE key
          setIsGameOver(false);
          setSupporterBadge(null);
        }}
        onWatchAd={() => {
          setIsGameOver(false);
        }}
      />

      {/* Daily Pulse Ticker — unobtrusive live feed in bottom-left corner */}
      <DailyPulseTicker
        topScores={topScoresQuery.data ?? []}
        isLoading={topScoresQuery.isLoading}
      />

      {/* Chrome Status Bar — native idle prompt, appears after 3.5s, dismisses instantly on first input */}
      <ChromeStatusBar gameStarted={gamesPlayed > 0 || isGameOver} />
    </div>
  );
}
